import { api } from '@/internationalization/api-client'

/**
 * Mirrors `domain.feedback` and migration 050's CHECK lists. A value added on
 * one side only is a 400 on submit or a category nobody can pick;
 * `tests/unit/test_feedback.py` compares the SQL and Python halves.
 */
export const FEEDBACK_TYPES = ['like', 'improve', 'feature_request', 'usability'] as const
export const FEEDBACK_CATEGORIES = [
  'recording',
  'transcript',
  'summary',
  'action_items',
  'search_ask',
  'analytics',
  'integrations',
  'settings',
  'other',
] as const

export type FeedbackType = (typeof FEEDBACK_TYPES)[number]
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]

/** Triage state. Mirrors `domain.feedback.FEEDBACK_STATUSES` and migration 050. */
export const FEEDBACK_STATUSES = ['new', 'reviewed', 'resolved'] as const
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number]

export const MAX_COMMENTS = 2000

export interface FeedbackRequest {
  type: FeedbackType
  category: FeedbackCategory
  rating?: number
  comments?: string
  /** The in-app route only; the server drops a query string regardless. */
  pagePath?: string
}

export const feedbackApi = {
  submit: (body: FeedbackRequest) => api.post<{ id: string }>('/feedback', body),
}

export interface FeedbackItem {
  id: string
  type: FeedbackType
  category: FeedbackCategory
  rating: number | null
  comments: string | null
  pagePath: string | null
  createdAt: string
  status: FeedbackStatus
  statusChangedAt: string | null
  /** Snapshots taken at submission: the inbox spans workspaces. */
  submitterName: string | null
  submitterEmail: string | null
  workspaceName: string | null
}

/**
 * The internal inbox. Restricted on the server to three named people; anyone
 * else gets a 404 (`feedback_inbox_not_available`).
 */
export const inboxApi = {
  list: (params: { status?: FeedbackStatus; limit: number; offset: number }) =>
    api.get<{ items: FeedbackItem[]; total: number }>('/feedback/inbox', { query: { ...params } }),
  setStatus: (id: string, status: FeedbackStatus) =>
    api.patch<FeedbackItem>(`/feedback/inbox/${id}`, { status }),
}
